import { type PropsWithChildren, lazy, useEffect } from 'react';
import { isAuthStatusUnauthenticatedSelector, userSelector } from '@lingoda/auth';
import { createDate, diffInUnits } from '@lingoda/dates';
import { isEasyGeneratorEnabled, isSelfLearningLimitAccessEnabled } from '@lingoda/feature-flags';
import { useSelector } from '@lingoda/hooks';
import {
    SettingsPages,
    bookingPreviewPath,
    goTo,
    refusedPaymentPath,
    studentChangedCoursePath,
    studentCourseOverviewPath,
    studentCreditsCheckoutPath,
    studentM2MCheckoutPath,
    studentPaymentPendingPath,
    studentPracticePath,
    studentReactivateCoursePath,
    studentSettingsPath,
    studentSprintCheckoutPath,
    studentStorePath,
    studentTeamsCheckoutPath,
    subscriptionsStorePath,
    useRouteMatcher,
} from '@lingoda/router';
import {
    SubscriptionStatus,
    areSubscriptionsLoadedSelector,
    currentSubscriptionSelector,
    hasSubscriptionSelector,
    lastSubscriptionSelector,
} from '@lingoda/subscriptions';
import { LinearProgress } from '@lingoda/ui';
import { hasOrientationEGSelector } from '@lingoda/modules';
import { SelfLearningAccess } from '@lingoda/learning';

const AccountClosed = lazy(() => import('apps/student/components/pages/store/AccountClosed'));
const RefusedPayment = lazy(() => import('apps/student/components/pages/store/RefusedPayment'));

export const AccessBlock = ({ children }: PropsWithChildren<unknown>) => {
    const areSubscriptionsLoaded = useSelector(areSubscriptionsLoadedSelector);
    const subscription = useSelector(currentSubscriptionSelector);
    const hasSubscription = useSelector(hasSubscriptionSelector);
    const lastSubscription = useSelector(lastSubscriptionSelector);
    const user = useSelector(userSelector);
    const subscriptionId = subscription ? subscription.id : 0;
    const subscriptionArgs = { subscription: subscriptionId };
    const isUnauthenticated = useSelector(isAuthStatusUnauthenticatedSelector);
    const isPracticePage = useRouteMatcher([studentPracticePath({ status: null as never })]);

    const isAllowedRoute = useRouteMatcher([
        refusedPaymentPath(subscriptionArgs),
        studentReactivateCoursePath(undefined),
        studentChangedCoursePath(undefined),
        { path: studentSettingsPath({ page: SettingsPages.None }), matchPrefix: true },
        { path: subscriptionsStorePath(undefined), matchPrefix: true },
        { path: studentCreditsCheckoutPath(undefined as never), matchPrefix: true },
        { path: studentM2MCheckoutPath(undefined as never), matchPrefix: true },
        { path: studentSprintCheckoutPath(undefined as never), matchPrefix: true },
        { path: studentTeamsCheckoutPath(undefined as never), matchPrefix: true },
        { path: bookingPreviewPath(undefined as never), matchPrefix: true },
        { path: studentPaymentPendingPath(undefined as never), matchPrefix: true },
        studentStorePath(undefined),
    ]);

    const hasOrientationEG = useSelector(hasOrientationEGSelector());

    useEffect(() => {
        // Redirect without blocking during 14-day grace period
        if (subscription?.isPaymentNeeded) {
            goTo(refusedPaymentPath({ subscription: subscriptionId }));
        }
    }, [subscription?.isPaymentNeeded, subscriptionId]);

    useEffect(() => {
        if (isPracticePage && isEasyGeneratorEnabled() && !hasOrientationEG) {
            goTo(studentCourseOverviewPath());
        }
    }, [isPracticePage, hasOrientationEG]);

    if (!isAllowedRoute && !isUnauthenticated) {
        if (!areSubscriptionsLoaded) {
            return <LinearProgress />;
        }

        // TODO: Refactor this when charging state machine is implemented on the backend

        // Grace period over, block with refused payment page
        if (
            subscription?.isPaymentNeeded &&
            diffInUnits('days', createDate(), subscription.expiresAt || createDate()) >= 14
        ) {
            return <RefusedPayment subscriptionId={subscriptionId} />;
        }

        // No current subscription, last one was suspended – meaning 60+ failures or chargeback
        if (!subscription && lastSubscription?.status === SubscriptionStatus.Suspended) {
            return <RefusedPayment subscriptionId={lastSubscription.id} />;
        }

        // No current subs, but had subs before - except B2B
        if (!subscription && hasSubscription && !user?.isCorporateClient) {
            return <AccountClosed />;
        }
    }

    return (
        <>
            {isSelfLearningLimitAccessEnabled() && <SelfLearningAccess />}
            {children}
        </>
    );
};
